import { useState, type FormEvent } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { teamLabel } from "@alpha/shared";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useProfile } from "../lib/me";
import type { Team, Tournament, Division, Pool, Profile, Registration } from "../lib/types";
import { registrationOpen } from "../lib/format";
import { poolsForDivision, resolvePoolId, poolReady } from "../lib/registrationPool";
import { registerTeamPath, TOURNAMENT_PARAM } from "../lib/routes";
import { trackRegistrationSubmitted } from "../lib/analytics";
import {
  Button,
  ButtonLink,
  Card,
  CheckCircleIcon,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  SignInCard,
  Spinner,
  stagger,
} from "../components/ui";

/** Register-a-team flow (spec §8.3): team → tournament → division → pool → contact. */
export function RegisterTeam() {
  const { idToken, ready, email } = useAuth();
  const profileQ = useProfile();
  // Set when the coach got here from a tournament page, which has already
  // asked the question the tournament dropdown is about to ask again.
  const [params] = useSearchParams();
  const fromTournamentId = params.get(TOURNAMENT_PARAM) ?? "";

  if (!ready) return <LoadingBlock />;
  if (!idToken) {
    return (
      <SignInCard
        title="Register a team"
        hint="Sign in to register one of your teams for a tournament."
        // The register button lives on a public page, so this is where a coach
        // signing in for the first time lands. Rebuilding the path instead of
        // echoing the current URL keeps anything else in the query string out
        // of the post-sign-in redirect.
        next={registerTeamPath(fromTournamentId || undefined)}
      />
    );
  }
  // Waiting on the profile is what lets the contact block start out filled:
  // the form seeds itself as it mounts, so it has to mount already knowing who
  // the account holder is.
  if (profileQ.isLoading) return <LoadingBlock label="Loading your profile…" />;

  return (
    <RegisterTeamForm
      profile={profileQ.data ?? null}
      accountEmail={email}
      fromTournamentId={fromTournamentId}
    />
  );
}

/**
 * The form, mounted only once the profile question has been settled.
 *
 * `profile` is null for a coach who has not been through the wizard yet, and
 * the request can fail outright; neither is worth blocking a registration
 * over, so the contact fields fall back to what the token knows and are left
 * empty rather than guessed at. They stay editable in every case — whoever
 * runs the team is not always the person holding the account.
 */
function RegisterTeamForm({
  profile,
  accountEmail,
  fromTournamentId,
}: {
  profile: Profile | null;
  accountEmail: string | null;
  fromTournamentId: string;
}) {
  const teamsQ = useQuery({
    queryKey: ["my-teams"],
    queryFn: () => api.get<{ teams: Team[] }>("/me/teams"),
  });
  const tournamentsQ = useQuery({
    queryKey: ["public-tournaments"],
    queryFn: () => api.get<{ tournaments: Tournament[] }>("/public/tournaments"),
  });

  const [teamId, setTeamId] = useState("");
  const [pickedTournamentId, setPickedTournamentId] = useState(fromTournamentId);
  const [divisionId, setDivisionId] = useState("");
  const [pickedPoolId, setPickedPoolId] = useState("");
  const [contactName, setContactName] = useState(profile?.displayName ?? "");
  const [contactEmail, setContactEmail] = useState(profile?.email ?? accountEmail ?? "");
  const [contactPhone, setContactPhone] = useState(profile?.phone ?? "");

  const teams = teamsQ.data?.teams ?? [];
  const openTournaments = (tournamentsQ.data?.tournaments ?? []).filter(registrationOpen);
  // The pick counts only while the dropdown is actually offering it. A pick
  // made in the dropdown always is; one seeded from the URL may not be — links
  // get shared, get stale, and get edited by hand, and a window can close
  // while the coach is still reading the page. Without this the <select> shows
  // "Select a tournament…" while the form quietly holds the id anyway, so the
  // division step opens and submit posts a tournament nobody chose.
  const tournamentId = openTournaments.some((t) => t.id === pickedTournamentId)
    ? pickedTournamentId
    : "";

  const bundleQ = useQuery({
    queryKey: ["bundle", tournamentId],
    enabled: !!tournamentId,
    queryFn: () =>
      api.get<{ divisions: Division[]; pools: Pool[] }>(
        `/public/tournaments/${tournamentId}/bundle`,
      ),
  });

  const divisions = bundleQ.data?.divisions ?? [];
  // The pool step's three rules live in `lib/registrationPool` — which pools a
  // division offers, whether the held pick is still one of them, and whether
  // the question has been answered at all. They are pure so they can be
  // tested; this step is behind a click, which the render tests cannot reach.
  const pools = poolsForDivision(bundleQ.data?.pools ?? [], divisionId);
  const poolId = resolvePoolId(pools, pickedPoolId);

  const submit = useMutation({
    mutationFn: () =>
      api.post<Registration>("/me/registrations", {
        tournamentId,
        teamId,
        divisionId,
        // Null rather than "" — the schema rejects an empty string, and this
        // is the same "no pool to pick" case the server records as null.
        poolId: poolId || null,
        contactName,
        contactEmail,
        contactPhone: contactPhone || undefined,
      }),
    // Here rather than in the `submit.isSuccess` branch below, which is a
    // render path and would count again on every re-render. `from` compares
    // against the id the URL arrived with, so it says "the tournament page's
    // button" only when that is the event actually being registered for.
    onSuccess: () =>
      trackRegistrationSubmitted(
        fromTournamentId && tournamentId === fromTournamentId ? "tournament_link" : "dropdown",
      ),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (
      teamId &&
      tournamentId &&
      divisionId &&
      poolReady(pools, poolId) &&
      contactName &&
      contactEmail
    ) {
      submit.mutate();
    }
  };

  if (submit.isSuccess) {
    return (
      <div className="mx-auto max-w-md animate-scale-in">
        <Card className="flex flex-col items-center gap-4 px-6 py-12 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brandText">
            <CheckCircleIcon className="h-7 w-7" />
          </span>
          <div className="space-y-1">
            <h1 className="font-display text-xl font-bold tracking-tight text-navy-950">
              Registration submitted
            </h1>
            <p className="text-sm text-slate-500">Your request is pending admin approval.</p>
          </div>
          <ButtonLink to="/me" variant="secondary" className="mt-2">
            Back to dashboard
          </ButtonLink>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        eyebrow="Registration"
        title="Register a team"
        subtitle="Pick your team, choose an open tournament, and tell us how to reach you."
      />

      <form onSubmit={onSubmit} className="animate-rise" style={stagger(1)}>
        <Card className="space-y-6 p-5 sm:p-8">
          <fieldset className="space-y-4">
            <StepChip n={1} label="Team & tournament" />

            <Field label="Team">
              <Select value={teamId} onChange={(e) => setTeamId(e.target.value)} required>
                <option value="">Select a team…</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {teamLabel(t)}
                  </option>
                ))}
              </Select>
              {!teamsQ.isLoading && teams.length === 0 ? (
                <span className="mt-1.5 block text-xs text-slate-400">
                  You don&rsquo;t have any teams yet —{" "}
                  <Link to="/me/teams" className="font-medium text-brandText hover:underline">
                    create one first
                  </Link>
                  .
                </span>
              ) : null}
            </Field>

            <Field label="Tournament">
              <Select
                value={tournamentId}
                onChange={(e) => {
                  setPickedTournamentId(e.target.value);
                  setDivisionId("");
                  setPickedPoolId("");
                }}
                required
              >
                <option value="">Select a tournament…</option>
                {openTournaments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
              {!tournamentsQ.isLoading && openTournaments.length === 0 ? (
                <span className="mt-1.5 block text-xs text-slate-400">
                  No tournaments are currently open for registration.
                </span>
              ) : null}
            </Field>

            {tournamentId ? (
              <div className="animate-slide-down">
                <Field label="Division">
                  <Select
                    value={divisionId}
                    onChange={(e) => {
                      setDivisionId(e.target.value);
                      // Pools belong to a division, so a pick made under the
                      // old one is not a pick under the new one.
                      setPickedPoolId("");
                    }}
                    required
                  >
                    <option value="">Select a division…</option>
                    {divisions.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            ) : null}

            {/* Only once the division has pools. An empty dropdown asks a
                question with no answer, and the organizer may simply not have
                built the pools yet — the server accepts a null pool for
                exactly that case, and an admin assigns one at approval. */}
            {divisionId && pools.length > 0 ? (
              <div className="animate-slide-down">
                <Field label="Pool">
                  <Select value={poolId} onChange={(e) => setPickedPoolId(e.target.value)} required>
                    <option value="">Select a pool…</option>
                    {pools.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            ) : null}
          </fieldset>

          <div className="border-t border-slate-100" />

          <fieldset className="space-y-4">
            <StepChip n={2} label="Contact" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Contact name">
                <Input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  required
                />
              </Field>
              <Field label="Contact email">
                <Input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                />
              </Field>
            </div>
            <Field label="Contact phone (optional)">
              <Input
                inputMode="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </Field>
          </fieldset>

          {submit.error instanceof ApiError ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-600/10">
              Could not submit ({submit.error.status}). Check the window is open and you own the
              team.
            </p>
          ) : null}

          <Button type="submit" disabled={submit.isPending} size="lg" className="w-full sm:w-auto">
            {submit.isPending ? (
              <>
                <Spinner className="h-4 w-4" />
                Submitting…
              </>
            ) : (
              "Submit registration"
            )}
          </Button>
        </Card>
      </form>
    </div>
  );
}

function StepChip({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-xs font-bold text-brandFg">
        {n}
      </span>
      <h2 className="font-display font-semibold text-navy-950">{label}</h2>
    </div>
  );
}
